import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { environment } from 'environments/environment';
import { map, Observable, ReplaySubject, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { UsersService } from 'app/services/users.service';
import { UserDTO } from 'app/model/user/user-dto.model';
import { FirebaseMessagingService } from 'app/services/firebase-messaging.service';

@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
    private apiUrl = `${environment.API_URL}/api/notifications`;
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<
        Notification[]
    >(1);
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _currentUser: UserDTO | null = null;

    constructor(
        private _httpClient: HttpClient,
        private _usersService: UsersService,
        private _firebaseMessaging: FirebaseMessagingService
    ) {
        this._usersService.getMyProfile().subscribe({
            next: (user: UserDTO) => {
                this._currentUser = user;
                console.log('Notifications - Utilisateur connecté:', {
                    id: user.id,
                    username: user.username,
                    roleId: user.roleId,
                    roleName: user.roleName
                });

                this.getAll().subscribe();
            },
            error: (err) => console.error('Erreur lors de la récupération du profil utilisateur', err)
        });

        this.listenToFirebaseMessages();

        this.initializeFirebaseMessaging();
    }

    get notifications$(): Observable<Notification[]> {
        return this._notifications.asObservable();
    }


    getAll(): Observable<Notification[]> {
        let params = new HttpParams();
        if (this._currentUser?.id) {
            params = params.set('userId', this._currentUser.id.toString());
        }
        if (this._currentUser?.roleId) {
            params = params.set('roleId', this._currentUser.roleId.toString());
        }

        return this._httpClient
            .get<Notification[]>(this.apiUrl, { params })
            .pipe(
                map(response => {
                    if (Array.isArray(response)) {
                        return response;
                    }
                    console.warn('Unexpected notifications response format:', response);
                    return [];
                }),
                tap((notifications: Notification[]) => {
                    this._notifications.next(notifications);
                })
            );
    }

    create(notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .post<Notification>(this.apiUrl, {
                        notification,
                    })
                    .pipe(
                        map((newNotification) => {
                            this._notifications.next([
                                ...notifications,
                                newNotification,
                            ]);
                            return newNotification;
                        })
                    )
            )
        );
    }

    update(id: number, notification: Notification): Observable<Notification> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .patch<Notification>(this.apiUrl, {
                        id,
                        notification,
                    })
                    .pipe(
                        map((updatedNotification: Notification) => {
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );

                            notifications[index] = updatedNotification;

                            this._notifications.next(notifications);

                            return updatedNotification;
                        })
                    )
            )
        );
    }

    delete(id: number): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .delete<boolean>(this.apiUrl, {
                        params: { id },
                    })
                    .pipe(
                        map((isSoftDeleted: boolean) => {
                            const index = notifications.findIndex(
                                (item) => item.id === id
                            );

                            notifications.splice(index, 1);

                            this._notifications.next(notifications);

                            return isSoftDeleted;
                        })
                    )
            )
        );
    }

    markAllAsRead(): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap((notifications) =>
                this._httpClient
                    .get<boolean>(`${this.apiUrl}/mark-all-as-read`)
                    .pipe(
                        map((isUpdated: boolean) => {
                            notifications.forEach((notification, index) => {
                                notifications[index].read = true;
                            });

                            this._notifications.next(notifications);

                            return isUpdated;
                        })
                    )
            )
        );
    }

    deleteAll(): Observable<boolean> {
        return this.notifications$.pipe(
            take(1),
            switchMap(() =>
                this._httpClient
                    .delete<boolean>(`${this.apiUrl}/delete-all`)
                    .pipe(
                        map((isSoftDeleted: boolean) => {
                            this._notifications.next([]);
                            return isSoftDeleted;
                        })
                    )
            )
        );
    }

    private async initializeFirebaseMessaging(): Promise<void> {
        const token = await this._firebaseMessaging.requestPermissionAndGetToken();
        if (token) {
            console.log('FCM Token obtenu:', token);
            this._httpClient.post(`${this.apiUrl.replace('/notifications', '')}/fcm/register`, { token })
                .subscribe({
                    next: () => console.log('Token FCM enregistré avec succès'),
                    error: (err) => console.error('Erreur lors de l\'enregistrement du token FCM:', err)
                });
        }
    }

    private listenToFirebaseMessages(): void {
        this._firebaseMessaging.message$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((payload) => {
                console.log('Notification Firebase reçue:', payload);
                this.getAll().subscribe();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
